import { useState, useEffect } from 'react';
import { Table, Button, Badge, Space, message, Tooltip, Select, Modal } from 'antd';
import { ReloadOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const IAM_API_URL = 'http://localhost:8000/api/v1';

interface UserSession {
  id: string;
  userId: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  token: string;
  deviceInfo?: {
    userAgent?: string;
    ip?: string;
    device?: string;
    browser?: string;
    os?: string;
  };
  isActive: boolean;
  createdAt: string;
  expiresAt: string;
  lastActiveAt: string;
  revokedAt?: string;
  revokedBy?: string;
}

interface SessionStats {
  total: number;
  active: number;
 expired: number;
}

export function SessionsPage() {
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [stats, setStats] = useState<SessionStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');

  useEffect(() => {
    fetchSessions();
    fetchStats();
  }, [selectedStatus]);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (selectedStatus !== 'all') params.append('isActive', selectedStatus);

      const response = await axios.get(`${IAM_API_URL}/sessions?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Backend returns { success: true, data: [...] }
      setSessions(response.data?.data || response.data || []); 
    } catch (error: any) {
      console.error('Error fetching sessions:', error);
      message.error(error.response?.data?.message || 'Failed to load sessions');
      setSessions([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${IAM_API_URL}/sessions/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Backend returns { success: true, data: {...} }
      setStats(response.data?.data || response.data || { total: 0, active: 0, expired: 0 });
    } catch (error: any) {
      console.error('Error fetching stats:', error);
      setStats({ total: 0, active: 0, expired: 0 }); // Set defaults on error
    }
  };

  const handleRevokeSession = async (id: string) => {
    Modal.confirm({
      title: 'Revoke Session',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to revoke this session? The user will be logged out.',
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${IAM_API_URL}/sessions/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          message.success('Session revoked successfully');
          fetchSessions();
          fetchStats();
        } catch (error: any) {
          message.error(error.response?.data?.message || 'Failed to revoke session');
        }
      },
    });
  };

  const handleRevokeAllUser = async (userId: string, userName: string) => {
    Modal.confirm({
      title: 'Revoke All Sessions',
      icon: <ExclamationCircleOutlined />,
      content: `Logout ${userName} from all devices?`,
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${IAM_API_URL}/sessions/user/${userId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          message.success(`All sessions revoked for ${userName}`);
          fetchSessions();
          fetchStats();
        } catch (error: any) {
          message.error(error.response?.data?.message || 'Failed to revoke sessions');
        }
      },
    });
  };

  const getDeviceIcon = (deviceInfo?: any) => {
    if (!deviceInfo) return '💻';
    const ua = deviceInfo.userAgent?.toLowerCase() || '';
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) return '📱';
    if (ua.includes('tablet') || ua.includes('ipad')) return '📟';
    return '💻';
  };

  const getStatusColor = (session: UserSession) => {
    if (session.revokedAt) return 'default';
    if (!session.isActive) return 'default';
    if (new Date(session.expiresAt) < new Date()) return 'error';
    
    const lastActive = new Date(session.lastActiveAt);
    const now = new Date();
    const hoursSinceActive = (now.getTime() - lastActive.getTime()) / (1000 * 60 * 60);
    
    if (hoursSinceActive < 1) return 'success';
    if (hoursSinceActive < 24) return 'warning';
    return 'default';
  };

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_: any, record: UserSession) => (
        <div>
          <div className="font-semibold text-gray-900">
            {record.user.firstName} {record.user.lastName}
          </div>
          <div className="text-sm text-gray-500">{record.user.email}</div>
        </div>
      ),
    },
    {
      title: 'Device',
      key: 'device',
      width: 300,
      render: (_: any, record: UserSession) => (
        <div className="flex items-center gap-2">
          <span className="text-2xl">{getDeviceIcon(record.deviceInfo)}</span>
          <div className="text-xs text-gray-600">
            {record.deviceInfo?.browser || 'Unknown'} • {record.deviceInfo?.os || 'Unknown'}
          </div>
        </div>
      ),
    },
    {
      title: 'IP Address',
      dataIndex: ['deviceInfo', 'ip'],
      key: 'ip',
      width: 150,
      render: (ip: string) => (
        <span className="text-sm text-gray-600">{ip || '-'}</span>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'created',
      width: 150,
      render: (date: string) => (
        <Tooltip title={new Date(date).toLocaleString()}>
          <span className="text-sm">{dayjs(date).fromNow()}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Last Active',
      dataIndex: 'lastActiveAt',
      key: 'lastActive',
      width: 150,
      render: (date: string) => (
        <Tooltip title={new Date(date).toLocaleString()}>
          <span className="text-sm">{dayjs(date).fromNow()}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_: any, record: UserSession) => {
        if (record.revokedAt) {
          return <Badge status="default" text="Revoked" />;
        }
        if (!record.isActive) {
          return <Badge status="default" text="Inactive" />;
        }
        if (new Date(record.expiresAt) < new Date()) {
          return <Badge status="error" text="Expired" />;
        }
        return <Badge status={getStatusColor(record)} text="Active" />;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_: any, record: UserSession) => (
        <Space size="small">
          {record.isActive && !record.revokedAt && (
            <>
              <Tooltip title="Revoke this session">
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleRevokeSession(record.id)}
                />
              </Tooltip>
              <Tooltip title="Revoke all user sessions">
                <Button
                  type="text"
                  danger
                  size="small"
                  onClick={() =>
                    handleRevokeAllUser(
                      record.userId,
                      `${record.user.firstName} ${record.user.lastName}`
                    )
                  }
                >
                  Logout User
                </Button>
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Session Management</h1>
              <p className="text-gray-600 mt-1">View and manage active user sessions</p>
            </div>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                fetchSessions();
                fetchStats();
              }}
              size="large"
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Sessions</div>
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Sessions</div>
              <div className="text-2xl font-bold text-green-600">{stats.active}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Expired Sessions</div>
              <div className="text-2xl font-bold text-red-600">{stats.expired}</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <Select
                style={{ width: '100%' }}
                value={selectedStatus}
                onChange={setSelectedStatus}
                options={[
                  { value: 'all', label: 'All Sessions' },
                  { value: 'true', label: 'Active Only' },
                  { value: 'false', label: 'Inactive Only' },
                ]}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow">
          <Table
            columns={columns}
            dataSource={sessions}
            loading={loading}
            rowKey="id"
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} sessions`,
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default SessionsPage;
